import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { organisationSchema } from "./organisations";

export const assetSetSchema = z.object({
  asset: z.string(),
  organisation: organisationSchema,
  extent: z
    .tuple([
      /* Can be a point, with west === east and north === south! */
      z.number() /* west */,
      z.number() /* south */,
      z.number() /* east */,
      z.number() /* north */,
    ])
    .nullable(),
});

export type AssetSet = z.infer<typeof assetSetSchema>;

const assetSetListSchema = z.array(assetSetSchema);

export function useAssetSets() {
  return useParsedQuery<AssetSet[]>(
    ["lizard-api-v4", "assetsets"],
    // Note the use of ?page_size=0, which simply returns an
    // array instead of being paginated.
    () => fetchWithError("/api/v4/assetsets/?page_size=0"),
    (json: unknown) =>
      // "building" is not supported yet due to some backend issue:
      // https://github.com/nens/nens-frontends/issues/1082
      assetSetListSchema.parse(json).filter((assetSet) => assetSet.asset !== "building")
  );
}

// Use this outside components, so the fetched asset set can still
// be stored in cache.
export async function fetchAndCacheAssetSet(
  queryClient: QueryClient,
  organisationUuid: string,
  assetType: string
): Promise<AssetSet | null> {
  try {
    const response = await fetchWithError(
      combineUrlAndParams("/api/v4/assetsets/", {
        asset: assetType,
        organisation__uuid: organisationUuid,
        page_size: 0,
      })
    );
    const assetSets = assetSetListSchema.parse(response);

    if (assetSets.length > 0) {
      queryClient.setQueryData(
        ["lizard-api-v4", "assetsets", organisationUuid, assetType],
        assetSets[0]
      );
      return assetSets[0];
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
}
